import { useEffect, useState } from "react";
import { Box, Drawer, Group, Loader, Paper, ScrollArea, Stack, Text, UnstyledButton } from "@mantine/core";
import { Alert, Badge, Button } from "@xplatform/design-system";
import { IconArrowLeft, IconGitCompare } from "@tabler/icons-react";
import type { FlowConfigViewModel, FlowVersionSummaryViewModel } from "@models/doctype";
import { useTranslation } from "@hooks/useTranslation";
import { docTypeApi } from "@src/services/doctypeApi";
import { translateFlowType } from "@utils/flowTypeTranslations";
import { diffFlowConfigs, diffLines, type FieldChange } from "@utils/flowConfigDiff";
import "./VersionHistoryDrawer.scss";

export const versionKey = (version: Pick<FlowVersionSummaryViewModel, "flowBaseId" | "version">) =>
    `${version.flowBaseId}-${version.version}`;

interface VersionHistoryDrawerProps {
    opened: boolean;
    onClose: () => void;
    docTypeId: string;
    versions: FlowVersionSummaryViewModel[];
    selected: FlowVersionSummaryViewModel | null;
    onSelect: (version: FlowVersionSummaryViewModel) => void;
    /** Open straight on the comparison of this version with the live one. */
    compareTarget?: FlowVersionSummaryViewModel | null;
}

const ChangeValue = ({ change }: { change: FieldChange }) => {
    const { t } = useTranslation();
    const multiline = (change.before ?? "").includes("\n") || (change.after ?? "").includes("\n")
        || (change.before ?? "").length > 80 || (change.after ?? "").length > 80;

    if (change.kind === "changed" && multiline) {
        const lines = diffLines(change.before ?? "", change.after ?? "");
        if (!lines) return <Text size="xs" c="dimmed">{t("admin.flowConfigs.history.tooLarge")}</Text>;
        return (
            <pre className="version-diff__lines">
                {lines.map((line, i) => (
                    <div key={i} className={`version-diff__line version-diff__line--${line.type}`}>
                        <span className="version-diff__sign">{line.type === "add" ? "+" : line.type === "del" ? "−" : " "}</span>
                        {line.text || " "}
                    </div>
                ))}
            </pre>
        );
    }
    return (
        <Group gap="xs" wrap="nowrap" align="flex-start" className="version-diff__inline">
            {change.before !== undefined && <code className="version-diff__before">{change.before}</code>}
            {change.before !== undefined && change.after !== undefined && <Text size="xs" c="dimmed">→</Text>}
            {change.after !== undefined && <code className="version-diff__after">{change.after}</code>}
        </Group>
    );
};

/**
 * Every version of the document type's flows, newest first, and what any of
 * them changes compared with the live one. Since every save creates a
 * version, this is where an admin finds out what a draft would change before
 * activating it.
 */
export const VersionHistoryDrawer = ({
    opened,
    onClose,
    docTypeId,
    versions,
    selected,
    onSelect,
    compareTarget,
}: VersionHistoryDrawerProps) => {
    const { t } = useTranslation();
    const live = versions.find((version) => version.isActive) ?? null;
    const [comparing, setComparing] = useState<FlowVersionSummaryViewModel | null>(null);
    const [changes, setChanges] = useState<FieldChange[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (opened) setComparing(compareTarget ?? null);
    }, [opened, compareTarget]);

    useEffect(() => {
        if (!comparing || !live) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setChanges(null);
        Promise.all([
            docTypeApi.getFlowDetailByVersion(docTypeId, live.flowBaseId, live.version),
            docTypeApi.getFlowDetailByVersion(docTypeId, comparing.flowBaseId, comparing.version),
        ])
            .then(([base, next]: [FlowConfigViewModel, FlowConfigViewModel]) => {
                if (!cancelled) setChanges(diffFlowConfigs(base, next));
            })
            .catch((err: unknown) => {
                if (!cancelled) setError(err instanceof Error ? err.message : String(err));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [comparing, live, docTypeId]);

    const label = (version: FlowVersionSummaryViewModel) => t("admin.flowConfigs.versions.label", { version: version.version });

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            position="right"
            size="min(760px, 96vw)"
            title={<Text fw={600} size="lg">{t("admin.flowConfigs.history.title")}</Text>}
            portalProps={{ target: "#admin-portal-root" }}
            scrollAreaComponent={ScrollArea.Autosize}
        >
            {comparing && live ? (
                <Stack gap="md">
                    <Group justify="space-between">
                        <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />} onClick={() => setComparing(null)}>
                            {t("admin.flowConfigs.history.backToList")}
                        </Button>
                        <Button
                            size="xs"
                            variant="light"
                            onClick={() => {
                                onSelect(comparing);
                                onClose();
                            }}
                        >
                            {t("admin.flowConfigs.history.open", { version: comparing.version })}
                        </Button>
                    </Group>
                    <Text size="sm">
                        {t("admin.flowConfigs.history.comparing", { version: comparing.version, live: live.version })}
                    </Text>
                    {loading && <Group justify="center" py="xl"><Loader size="sm" /></Group>}
                    {error && <Alert color="red">{error}</Alert>}
                    {changes && changes.length === 0 && (
                        <Text size="sm" c="dimmed">{t("admin.flowConfigs.history.noChanges")}</Text>
                    )}
                    {changes && changes.length > 0 && (
                        <Text size="xs" c="dimmed">{t("admin.flowConfigs.history.changeCount", { count: changes.length })}</Text>
                    )}
                    {changes?.map((change) => (
                        <Paper key={change.path} withBorder p="sm" radius="sm">
                            <Group gap="xs" mb="xs" wrap="nowrap">
                                <Badge
                                    size="xs"
                                    variant="light"
                                    color={change.kind === "added" ? "green" : change.kind === "removed" ? "red" : "blue"}
                                >
                                    {t(`admin.flowConfigs.history.kind.${change.kind}`)}
                                </Badge>
                                <Text size="xs" fw={600} ff="monospace" truncate title={change.path}>{change.path}</Text>
                            </Group>
                            <ChangeValue change={change} />
                        </Paper>
                    ))}
                </Stack>
            ) : (
                <Stack gap="xs">
                    {versions.map((version) => {
                        const isSelected = selected ? versionKey(selected) === versionKey(version) : false;
                        return (
                            <Group
                                key={versionKey(version)}
                                className="version-history__row"
                                data-selected={isSelected || undefined}
                                justify="space-between"
                                wrap="nowrap"
                            >
                                <UnstyledButton
                                    className="version-history__open"
                                    onClick={() => {
                                        onSelect(version);
                                        onClose();
                                    }}
                                >
                                    <Group gap="sm" wrap="nowrap">
                                        <Text fw={600} size="sm" w={44}>{label(version)}</Text>
                                        <Badge size="sm" variant={version.isActive ? "filled" : "light"} color={version.isActive ? "green" : "gray"}>
                                            {version.isActive ? t("admin.flowConfigs.status.live") : t("admin.flowConfigs.status.draft")}
                                        </Badge>
                                        <Box miw={0}>
                                            <Text size="sm" truncate>{translateFlowType(version.flowType, t)}</Text>
                                            <Text size="xs" c="dimmed" truncate>
                                                {version.createdAt?.toLocaleString() ?? "-"}
                                                {(version.updatedBy || version.createdBy)
                                                    && ` · ${version.updatedBy || version.createdBy}`}
                                            </Text>
                                        </Box>
                                    </Group>
                                </UnstyledButton>
                                {live && !version.isActive && (
                                    <Button
                                        size="compact-xs"
                                        variant="subtle"
                                        leftSection={<IconGitCompare size={14} />}
                                        onClick={() => setComparing(version)}
                                    >
                                        {t("admin.flowConfigs.history.compare")}
                                    </Button>
                                )}
                            </Group>
                        );
                    })}
                </Stack>
            )}
        </Drawer>
    );
};
